import { msalInstance } from './msalInstance';
import { getApiScopes, getConfig } from '../authConfig';

let pendingAccessTokenPromise: Promise<string | null> | null = null;

function sleep(ms: number): Promise<void> {
  return new Promise(resolve => setTimeout(resolve, ms));
}

async function acquireBackendAccessToken(): Promise<string | null> {
  if (pendingAccessTokenPromise) {
    return pendingAccessTokenPromise;
  }

  pendingAccessTokenPromise = (async () => {
    const accounts = msalInstance.getAllAccounts();
    if (accounts.length === 0) {
      console.debug('[apiFetch] No accounts available, proceeding without token');
      return null;
    }

    const scopes = getApiScopes();
    if (scopes.length === 0) {
      console.debug('[apiFetch] No API scopes configured, proceeding without token');
      return null;
    }

    const account = accounts[0];

    for (let attempt = 0; attempt < 3; attempt += 1) {
      try {
        const tokenResponse = await msalInstance.acquireTokenSilent({
          scopes,
          account,
        });

        return tokenResponse.accessToken;
      } catch (silentError: any) {
        const errorCode = silentError?.errorCode || silentError?.name;

        if (errorCode === 'interaction_in_progress') {
          console.debug('[apiFetch] Token interaction already in progress, retrying silent acquisition');
          await sleep(400 * (attempt + 1));
          continue;
        }

        console.warn('[apiFetch] Silent token acquisition failed:', silentError?.message || silentError);
        // Keep background API calls non-interactive to avoid popup loops.
        return null;
      }
    }

    console.warn('[apiFetch] Exhausted token acquisition retries; proceeding without token');
    return null;
  })().finally(() => {
    pendingAccessTokenPromise = null;
  });

  return pendingAccessTokenPromise;
}

/**
 * Drop-in replacement for fetch() that automatically attaches an Azure AD
 * Bearer token to every backend API request.
 */
export async function apiFetch(
  url: string,
  options: RequestInit = {}
): Promise<Response> {
  const config = getConfig();
  const backendRequiresAuth = config.BACKEND_REQUIRES_AUTH === true || config.BACKEND_REQUIRES_AUTH === "true";
  if (!backendRequiresAuth) {
    console.debug('[apiFetch] Backend does not require authentication, skipping token acquisition');
    return fetch(url, options);
  }

  const accessToken = await acquireBackendAccessToken();
  if (!accessToken) {
    return fetch(url, options);
  }

  const headers = new Headers(options.headers as HeadersInit | undefined);
  headers.set('Authorization', `Bearer ${accessToken}`);
  return fetch(url, { ...options, headers });
}
